/**
 * بوابة Supabase.
 *
 * كل قراءة/كتابة تمرّ عبر RLS. الأوامر الحساسة تُنفَّذ عبر دوال RPC
 * (SECURITY DEFINER) تحتوي التحقق الكامل، ولا تعتمد على تحقق الواجهة.
 *
 * ملاحظة معمارية: PostgREST لا يوفّر معاملات عبر طلبات متعددة، لذلك
 * `transaction()` لا يضمن ذرّية بين استدعاءات منفصلة. العمليات التي تحتاج
 * ذرّية (إنشاء مهمة، إكمال، اعتماد، توليد مواعيد، حذف احتفاظ) لها دوال RPC
 * مخصّصة. انظر docs/DATABASE.md.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { AppError } from '@/domain/errors';
import { newId } from '@/domain/ids';
import type {
  AuthGateway,
  AuditEventInput,
  CommandContext,
  DataGateway,
  Filter,
  FilterOperator,
  GatewayCommands,
  PlatformBranding,
  PlatformActivityEvent,
  PlatformActivityRetentionSettings,
  PlatformSupportSession,
  QueryOptions,
  Repository,
  RoleDeletionImpact,
  Row,
  TableName,
} from '../types';
import { DEFAULT_PLATFORM_BRANDING } from '../types';
import { DEFAULT_UPLOAD_POLICY } from '@/domain/settings';
import { COMPANY_PERMISSIONS, type PermissionKey } from '@/domain/permissions';
import type { PlatformOperationsSettings } from '@/domain/billing';
import type { Attachment, Role, ThemeTokens } from '@/domain/types';
import { buildStoragePath, sha256Hex } from '../blob-store';
import { getSupabaseClient } from './client';
import { SupabaseAuthGateway } from './auth';
import { camelToSnake, rowFromDb, rowToDb, rowsFromDb } from './mappers';
import {
  enqueueSyncOperation,
  getLocalBlobUrl,
  isBrowserOnline,
  isNetworkError,
  mirrorRowsToLocal,
  mirrorRowToLocal,
  offlineCancelTask,
  offlineCompleteTask,
  offlineCreateTask,
  offlineDecideApproval,
  offlineDeleteAttachment,
  offlineDeleteTask,
  offlineReopenTask,
  offlineUpdateTask,
  offlineUploadAttachment,
  readLocalCount,
  readLocalGet,
  readLocalList,
  readLocalQuery,
  registerOnlineSyncExecutor,
  removeLocalBlob,
  removeRowFromLocal,
  saveLocalBlob,
} from '../hybrid-sync';

const BUCKET = 'attachments';

type DbRow = Record<string, unknown>;
type DbError = { message: string; code?: string; details?: string; hint?: string };
type DbResponse<T> = { data: T; error: DbError | null; count?: number | null };

interface DbQuery extends PromiseLike<DbResponse<unknown>> {
  select(columns?: string, options?: Record<string, unknown>): DbQuery;
  insert(values: unknown): DbQuery;
  update(values: unknown): DbQuery;
  delete(): DbQuery;
  eq(column: string, value: unknown): DbQuery;
  neq(column: string, value: unknown): DbQuery;
  in(column: string, values: unknown[]): DbQuery;
  is(column: string, value: null): DbQuery;
  not(column: string, operator: string, value: unknown): DbQuery;
  gt(column: string, value: unknown): DbQuery;
  gte(column: string, value: unknown): DbQuery;
  lt(column: string, value: unknown): DbQuery;
  lte(column: string, value: unknown): DbQuery;
  contains(column: string, value: unknown[]): DbQuery;
  order(column: string, options?: Record<string, unknown>): DbQuery;
  range(from: number, to: number): DbQuery;
  limit(count: number): DbQuery;
  maybeSingle(): PromiseLike<DbResponse<DbRow | null>>;
  single(): PromiseLike<DbResponse<DbRow>>;
}

function translateError(error: DbError, fallback: string): AppError {
  const message = error.message.toLowerCase();
  // رسائل دوال قاعدة البيانات التجارية مكتوبة بالعربية ومقصودة للعرض،
  // بخلاف أخطاء PostgreSQL الداخلية التي تبقى عامة.
  if (
    /[\u0600-\u06ff]/.test(error.message) &&
    ['22023', '23505', '42501', 'P0001'].includes(error.code ?? '')
  ) {
    const code =
      error.code === '42501'
        ? 'forbidden'
        : error.code === '23505'
          ? 'conflict'
          : error.code === '22023'
            ? 'validation'
            : 'unknown';
    return new AppError(code, {
      userMessage: error.message,
      technicalMessage: error.message,
      cause: error,
    });
  }
  if (error.code === '42501' || message.includes('row-level security') || message.includes('permission denied')) {
    return AppError.forbidden('لا تملك صلاحية تنفيذ هذه العملية.');
  }
  if (error.code === '23505' || message.includes('duplicate key')) {
    return AppError.conflict('يوجد سجل مماثل مسبقًا.');
  }
  if (error.code === 'PGRST116' || message.includes('0 rows')) {
    return AppError.notFound();
  }
  if (error.code === 'PGRST202' || message.includes('could not find the function') || message.includes('schema cache')) {
    return AppError.unsupported('إجراء قاعدة البيانات المطلوب غير متاح في Supabase. تحقق من تحديث المخطط قبل إعادة المحاولة.');
  }
  if (error.code === '23514' || error.code === '23503' || error.code === '22P02') {
    return AppError.validation('البيانات غير صالحة أو تشير إلى سجل غير موجود.');
  }
  if (error.code === 'PGRST301' || message.includes('jwt')) {
    return AppError.unauthenticated();
  }
  if (message.includes('fetch') || message.includes('network')) {
    return new AppError('network', { userMessage: 'تعذّر الاتصال بالخادم.', cause: error });
  }
  return new AppError('unknown', { userMessage: fallback, cause: error });
}

async function run<T>(query: PromiseLike<DbResponse<T>>, fallback: string): Promise<DbResponse<T>> {
  const result = await query;
  if (result.error) throw translateError(result.error, fallback);
  return result;
}

function applyFilter(query: DbQuery, filter: Filter<Record<string, unknown>> | undefined): DbQuery {
  if (!filter) return query;
  let current = query;
  for (const [field, expression] of Object.entries(filter)) {
    if (!expression) continue;
    const column = camelToSnake(field);
    current = applyOperator(current, column, expression as FilterOperator);
  }
  return current;
}

function applyOperator(query: DbQuery, column: string, expression: FilterOperator): DbQuery {
  switch (expression.op) {
    case 'eq':
      return expression.value === null ? query.is(column, null) : query.eq(column, expression.value);
    case 'neq':
      return query.neq(column, expression.value);
    case 'in':
      return query.in(column, expression.value);
    case 'isNull':
      return query.is(column, null);
    case 'isNotNull':
      return query.not(column, 'is', null);
    case 'gt':
      return query.gt(column, expression.value);
    case 'gte':
      return query.gte(column, expression.value);
    case 'lt':
      return query.lt(column, expression.value);
    case 'lte':
      return query.lte(column, expression.value);
    case 'contains':
      return query.contains(column, expression.value);
    default:
      return query;
  }
}

class SupabaseRepository<T extends TableName> implements Repository<Row<T>> {
  constructor(
    private readonly client: SupabaseClient,
    private readonly table: T,
  ) {}

  private buildQuery(options: QueryOptions<Row<T>> = {}, count = false): DbQuery {
    const builder = this.client
      .from(this.table)
      .select('*', count ? { count: 'exact' } : {}) as unknown as DbQuery;
    let current = applyFilter(builder, options.filter as Filter<Record<string, unknown>> | undefined);
    for (const order of options.orderBy ?? []) {
      current = current.order(camelToSnake(order.field), { ascending: order.direction === 'asc' });
    }
    if (options.offset !== undefined || options.limit !== undefined) {
      const from = options.offset ?? 0;
      const to = options.limit !== undefined ? from + options.limit - 1 : from + 999;
      current = current.range(from, to);
    }
    return current;
  }

  async list(options: QueryOptions<Row<T>> = {}): Promise<Row<T>[]> {
    if (!isBrowserOnline()) {
      return readLocalList(this.table, options);
    }
    try {
      const result = await run(this.buildQuery(options) as PromiseLike<DbResponse<DbRow[]>>, 'تعذّر تحميل السجلات.');
      const rows = rowsFromDb<Row<T>>(result.data ?? []);
      void mirrorRowsToLocal(this.table, rows);
      return rows;
    } catch (error) {
      if (isNetworkError(error)) {
        return readLocalList(this.table, options);
      }
      throw error;
    }
  }

  async query(options: QueryOptions<Row<T>> = {}): Promise<{ rows: Row<T>[]; total: number | null }> {
    if (!isBrowserOnline()) {
      return readLocalQuery(this.table, options);
    }
    try {
      const result = await run(
        this.buildQuery(options, true) as PromiseLike<DbResponse<DbRow[]>>,
        'تعذّر تحميل السجلات.',
      );
      const rows = rowsFromDb<Row<T>>(result.data ?? []);
      void mirrorRowsToLocal(this.table, rows);
      return { rows, total: result.count ?? null };
    } catch (error) {
      if (isNetworkError(error)) {
        return readLocalQuery(this.table, options);
      }
      throw error;
    }
  }

  async get(id: string): Promise<Row<T> | null> {
    if (!isBrowserOnline()) {
      return readLocalGet(this.table, id);
    }
    try {
      const result = await run(
        this.client.from(this.table).select('*').eq('id', id).maybeSingle() as unknown as PromiseLike<
          DbResponse<DbRow | null>
        >,
        'تعذّر تحميل السجل.',
      );
      const row = result.data ? rowFromDb<Row<T>>(result.data) : null;
      if (row) void mirrorRowToLocal(this.table, row);
      return row;
    } catch (error) {
      if (isNetworkError(error)) {
        return readLocalGet(this.table, id);
      }
      throw error;
    }
  }

  async insertOnline(row: Row<T>): Promise<Row<T>> {
    const result = await run(
      this.client.from(this.table).insert(rowToDb(row as unknown as DbRow)).select('*').single() as unknown as PromiseLike<
        DbResponse<DbRow>
      >,
      'تعذّر إنشاء السجل.',
    );
    const saved = rowFromDb<Row<T>>(result.data);
    void mirrorRowToLocal(this.table, saved);
    return saved;
  }

  async insert(row: Row<T>): Promise<Row<T>> {
    await mirrorRowToLocal(this.table, row);
    if (!isBrowserOnline()) {
      await enqueueSyncOperation('table.insert', { table: this.table, row });
      return row;
    }
    try {
      return await this.insertOnline(row);
    } catch (error) {
      if (isNetworkError(error)) {
        await enqueueSyncOperation('table.insert', { table: this.table, row });
        return row;
      }
      throw error;
    }
  }

  async insertManyOnline(rows: Row<T>[]): Promise<Row<T>[]> {
    if (rows.length === 0) return [];
    const result = await run(
      this.client
        .from(this.table)
        .insert(rows.map((row) => rowToDb(row as unknown as DbRow)))
        .select('*') as unknown as PromiseLike<DbResponse<DbRow[]>>,
      'تعذّر إنشاء السجلات.',
    );
    const saved = rowsFromDb<Row<T>>(result.data ?? []);
    void mirrorRowsToLocal(this.table, saved);
    return saved;
  }

  async insertMany(rows: Row<T>[]): Promise<Row<T>[]> {
    if (rows.length === 0) return [];
    await mirrorRowsToLocal(this.table, rows);
    if (!isBrowserOnline()) {
      await enqueueSyncOperation('table.insertMany', { table: this.table, rows });
      return rows;
    }
    try {
      return await this.insertManyOnline(rows);
    } catch (error) {
      if (isNetworkError(error)) {
        await enqueueSyncOperation('table.insertMany', { table: this.table, rows });
        return rows;
      }
      throw error;
    }
  }

  async updateOnline(id: string, patch: Partial<Row<T>>): Promise<Row<T>> {
    const result = await run(
      this.client
        .from(this.table)
        .update(rowToDb(patch as unknown as DbRow))
        .eq('id', id)
        .select('*')
        .single() as unknown as PromiseLike<DbResponse<DbRow>>,
      'تعذّر تحديث السجل.',
    );
    const saved = rowFromDb<Row<T>>(result.data);
    void mirrorRowToLocal(this.table, saved);
    return saved;
  }

  async update(id: string, patch: Partial<Row<T>>): Promise<Row<T>> {
    const existing = await readLocalGet(this.table, id);
    const optimistic = { ...(existing ?? {}), ...patch, id } as Row<T>;
    await mirrorRowToLocal(this.table, optimistic);
    if (!isBrowserOnline()) {
      await enqueueSyncOperation('table.update', { table: this.table, id, patch });
      return optimistic;
    }
    try {
      return await this.updateOnline(id, patch);
    } catch (error) {
      if (isNetworkError(error)) {
        await enqueueSyncOperation('table.update', { table: this.table, id, patch });
        return optimistic;
      }
      throw error;
    }
  }

  async updateIf(id: string, patch: Partial<Row<T>>, expectedVersion: number): Promise<Row<T> | null> {
    if (!isBrowserOnline()) {
      return this.update(id, patch);
    }
    try {
      const result = await run(
        this.client
          .from(this.table)
          .update(rowToDb(patch as unknown as DbRow))
          .eq('id', id)
          .eq('version', expectedVersion)
          .select('*')
          .maybeSingle() as unknown as PromiseLike<DbResponse<DbRow | null>>,
        'تعذّر تحديث السجل.',
      );
      const saved = result.data ? rowFromDb<Row<T>>(result.data) : null;
      if (saved) void mirrorRowToLocal(this.table, saved);
      return saved;
    } catch (error) {
      if (isNetworkError(error)) {
        return this.update(id, patch);
      }
      throw error;
    }
  }

  async updateMany(filter: Filter<Row<T>>, patch: Partial<Row<T>>): Promise<number> {
    let query = this.client.from(this.table).update(rowToDb(patch as unknown as DbRow)) as unknown as DbQuery;
    query = applyFilter(query, filter as Filter<Record<string, unknown>>);
    const result = await run(
      (query as unknown as { select: (c: string, o: Record<string, unknown>) => PromiseLike<DbResponse<DbRow[]>> }).select(
        '*',
        { count: 'exact' },
      ),
      'تعذّر تحديث السجلات.',
    );
    return result.data?.length ?? 0;
  }

  async removeOnline(id: string): Promise<void> {
    await run(
      this.client.from(this.table).delete().eq('id', id) as unknown as PromiseLike<DbResponse<null>>,
      'تعذّر حذف السجل.',
    );
    void removeRowFromLocal(this.table, id);
  }

  async remove(id: string): Promise<void> {
    await removeRowFromLocal(this.table, id);
    if (!isBrowserOnline()) {
      await enqueueSyncOperation('table.remove', { table: this.table, id });
      return;
    }
    try {
      await this.removeOnline(id);
    } catch (error) {
      if (isNetworkError(error)) {
        await enqueueSyncOperation('table.remove', { table: this.table, id });
        return;
      }
      throw error;
    }
  }

  async removeMany(filter: Filter<Row<T>>): Promise<number> {
    let query = this.client.from(this.table).delete() as unknown as DbQuery;
    query = applyFilter(query, filter as Filter<Record<string, unknown>>);
    const result = await run(
      (query as unknown as { select: (c: string, o: Record<string, unknown>) => PromiseLike<DbResponse<DbRow[]>> }).select(
        '*',
        { count: 'exact' },
      ),
      'تعذّر حذف السجلات.',
    );
    return result.data?.length ?? 0;
  }

  async count(filter?: Filter<Row<T>>): Promise<number> {
    if (!isBrowserOnline()) {
      return readLocalCount(this.table, filter);
    }
    try {
      let query = this.client
        .from(this.table)
        .select('*', { count: 'exact', head: true }) as unknown as DbQuery;
      query = applyFilter(query, filter as Filter<Record<string, unknown>> | undefined);
      const result = await run(query as PromiseLike<DbResponse<null>>, 'تعذّر حساب السجلات.');
      return result.count ?? 0;
    } catch (error) {
      if (isNetworkError(error)) {
        return readLocalCount(this.table, filter);
      }
      throw error;
    }
  }
}

function asRecord(value: unknown): DbRow {
  return (value ?? {}) as DbRow;
}

function mapResult<T>(value: unknown): T {
  return rowFromDb<T>(asRecord(value));
}

function mapRowsResult<T>(value: unknown): T[] {
  if (!Array.isArray(value)) {
    throw new TypeError('استجابة الخادم المتوقعة يجب أن تكون قائمة.');
  }
  return value.map((row) => asRecord(row) as T);
}

export class SupabaseGateway implements DataGateway {
  readonly kind = 'supabase' as const;
  readonly supportsOffline = true;
  readonly auth: AuthGateway = new SupabaseAuthGateway();
  readonly commands: GatewayCommands;

  private readonly client: SupabaseClient;
  private readonly repositories = new Map<TableName, SupabaseRepository<TableName>>();

  constructor(client: SupabaseClient = getSupabaseClient()) {
    this.client = client;
    this.commands = this.createCommands();
    this.registerSyncExecutor();
  }

  private registerSyncExecutor(): void {
    registerOnlineSyncExecutor({
      insertRow: async (table, row) => (this.table(table) as SupabaseRepository<typeof table>).insertOnline(row as unknown as Row<typeof table>),
      insertManyRows: async (table, rows) => (this.table(table) as SupabaseRepository<typeof table>).insertManyOnline(rows as unknown as Row<typeof table>[]),
      updateRow: async (table, id, patch) => (this.table(table) as SupabaseRepository<typeof table>).updateOnline(id, patch as unknown as Partial<Row<typeof table>>),
      removeRow: async (table, id) => (this.table(table) as SupabaseRepository<typeof table>).removeOnline(id),
      createTask: (input) => this.createTaskOnline(input),
      updateTask: (input) => this.updateTaskOnline(input),
      completeTask: (input) => this.completeTaskOnline(input),
      decideApproval: (input) => this.decideApprovalOnline(input),
      reopenTask: (input) => this.reopenTaskOnline(input),
      cancelTask: (input) => this.cancelTaskOnline(input),
      deleteTask: (input) => this.deleteTaskOnline(input),
      uploadAttachmentWithRecord: (attachment, bytes) => this.uploadAttachmentRecordOnline(attachment, bytes),
      deleteAttachment: (input) => this.deleteAttachmentOnline(input),
    });
  }

  table<T extends TableName>(name: T): Repository<Row<T>> {
    const existing = this.repositories.get(name);
    if (existing) return existing as unknown as Repository<Row<T>>;
    const repository = new SupabaseRepository<T>(this.client, name);
    this.repositories.set(name, repository as unknown as SupabaseRepository<TableName>);
    return repository;
  }

  /**
   * PostgREST لا يضمن معاملة واحدة بين عدة طلبات. ننفّذ العمل كما هو،
   * والعمليات الحساسة تُستدعى عبر RPC ذرّي. لا نَدّعي ذرّية غير موجودة.
   */
  async transaction<T>(work: () => Promise<T>): Promise<T> {
    return work();
  }

  async getAccessSnapshot(companyId?: string | null) {
    const cacheKey = `taskflow.access_snapshot:${companyId ?? 'default'}`;
    const readCached = () => {
      try {
        const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(cacheKey) : null;
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    };
    if (!isBrowserOnline()) {
      const cached = readCached();
      if (cached) return cached;
    }
    try {
      const data = mapResult<{
        companyId: string | null;
        membershipId: string | null;
        isPlatformOwner: boolean;
        platformPermissions: string[];
        permissions: string[];
        companyWide: boolean;
        branchIds: string[];
        brandIds: string[];
        membershipIds: string[];
        supervisedMembershipIds: string[];
      }>(await this.rpc('my_access_context', { p_company_id: companyId ?? null }));
      const supportSession = await this.getPlatformSupportSession().catch(() => null);
      const isPlatformSupport = Boolean(supportSession && supportSession.companyId === data.companyId);
      const snapshot = {
        companyId: data.companyId,
        membershipId: data.membershipId,
        isPlatformOwner: data.isPlatformOwner,
        isPlatformSupport,
        platformSupportSessionId: isPlatformSupport ? supportSession!.id : null,
        platformPermissions: data.platformPermissions as PermissionKey[],
        permissions: isPlatformSupport
          ? [...new Set([...(data.permissions as PermissionKey[]), ...COMPANY_PERMISSIONS])]
          : data.permissions as PermissionKey[],
        companyWide: isPlatformSupport || data.companyWide,
        branchIds: isPlatformSupport ? [] : data.branchIds,
        brandIds: isPlatformSupport ? [] : data.brandIds,
        membershipIds: isPlatformSupport ? [] : data.membershipIds,
        supervisedMembershipIds: isPlatformSupport ? [] : data.supervisedMembershipIds,
      };
      try {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(cacheKey, JSON.stringify(snapshot));
        }
      } catch {}
      return snapshot;
    } catch (error) {
      if (isNetworkError(error)) {
        const cached = readCached();
        if (cached) return cached;
      }
      throw error;
    }
  }

  async getPlatformSupportSession(): Promise<PlatformSupportSession | null> {
    const result = await this.rpc('current_platform_support_session');
    if (!result || typeof result !== 'object') return null;
    return mapResult<PlatformSupportSession>(result);
  }

  async beginPlatformSupportAccess(input: { companyId: string; reason: string }): Promise<PlatformSupportSession> {
    return mapResult<PlatformSupportSession>(await this.rpc('begin_platform_support_access', {
      p_company_id: input.companyId,
      p_reason: input.reason,
    }));
  }

  async endPlatformSupportAccess(sessionId: string): Promise<void> {
    await this.rpc('end_platform_support_access', { p_session_id: sessionId });
  }

  async recordAudit(input: AuditEventInput): Promise<void> {
    if (!isBrowserOnline()) return;
    try {
      await this.rpc('record_audit', {
        p_company_id: input.companyId,
        p_action: input.action,
        p_entity_type: input.entityType,
        p_entity_id: input.entityId,
        p_before: input.before ?? null,
        p_after: input.after ?? null,
      });
    } catch (error) {
      if (!isNetworkError(error)) throw error;
    }
  }

  private async rpc<T = unknown>(
    name: string,
    args: Record<string, unknown> = {},
    fallback = 'تعذّر تنفيذ العملية على الخادم.',
  ): Promise<T> {
    const { data, error } = await this.client.rpc(name, rowToDb(args));
    if (error) throw translateError(error, fallback);
    return data as T;
  }

  private commandContext(): CommandContext {
    return { requestId: newId() };
  }

  private async resolveOfflineCompanyId(input: { categoryId?: string; actorMembershipId?: string | null }): Promise<string> {
    if (input.actorMembershipId) {
      const m = await readLocalGet('memberships', input.actorMembershipId);
      if (m?.companyId) return m.companyId;
    }
    if (input.categoryId) {
      const c = await readLocalGet('task_categories', input.categoryId);
      if (c?.companyId) return c.companyId;
    }
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('taskflow.auth.snapshot_cache') : null;
      if (raw) {
        const parsed = JSON.parse(raw) as { activeCompanyId?: string | null };
        if (parsed.activeCompanyId) return parsed.activeCompanyId;
      }
    } catch {}
    return '';
  }

  private async createTaskOnline(input: import('../types').CreateTaskCommandInput): Promise<Row<'tasks'>> {
    const requestId = this.commandContext().requestId;
    const data = input.actorMembershipId === null
      ? await this.rpc('platform_support_create_task', {
          p_task: rowToDb(input.task), p_assignee_ids: input.task.assigneeMembershipIds,
          p_steps: input.steps ?? [], p_request_id: requestId,
        })
      : await this.rpc('create_task', {
          p_task: rowToDb({ ...input.task, actorMembershipId: input.actorMembershipId }),
          p_assignee_ids: input.task.assigneeMembershipIds, p_steps: input.steps ?? [],
          p_actor_membership_id: input.actorMembershipId, p_request_id: requestId,
        });
    const created = mapResult<Row<'tasks'>>(data);
    void mirrorRowToLocal('tasks', created);
    return created;
  }

  private async updateTaskOnline(input: import('../types').UpdateTaskCommandInput): Promise<Row<'tasks'>> {
    const requestId = this.commandContext().requestId;
    const data = input.actorMembershipId === null
      ? await this.rpc('platform_support_update_task', { p_task_id: input.taskId, p_patch: rowToDb(input.patch), p_request_id: requestId })
      : await this.rpc('update_task', { p_task_id: input.taskId, p_patch: rowToDb(input.patch), p_actor_membership_id: input.actorMembershipId, p_request_id: requestId });
    const updated = mapResult<Row<'tasks'>>(data);
    void mirrorRowToLocal('tasks', updated);
    return updated;
  }

  private async completeTaskOnline(input: import('../types').CompleteTaskInput): Promise<Row<'tasks'>> {
    const requestId = this.commandContext().requestId;
    const data = input.membershipId === null
      ? await this.rpc('platform_support_complete_task', { p_task_id: input.taskId, p_proof_note: input.proofNote, p_attachment_ids: input.attachmentIds, p_request_id: requestId })
      : await this.rpc('complete_task', { p_task_id: input.taskId, p_membership_id: input.membershipId, p_proof_note: input.proofNote, p_attachment_ids: input.attachmentIds, p_request_id: requestId });
    const completed = mapResult<Row<'tasks'>>(data);
    void mirrorRowToLocal('tasks', completed);
    return completed;
  }

  private async decideApprovalOnline(input: import('../types').ApproveTaskInput): Promise<Row<'tasks'>> {
    const requestId = this.commandContext().requestId;
    const data = input.membershipId === null
      ? await this.rpc('platform_support_decide_task_approval', { p_task_id: input.taskId, p_decision: input.decision, p_reason: input.reason, p_request_id: requestId })
      : await this.rpc('decide_task_approval', { p_task_id: input.taskId, p_membership_id: input.membershipId, p_decision: input.decision, p_reason: input.reason, p_request_id: requestId });
    const decided = mapResult<Row<'tasks'>>(data);
    void mirrorRowToLocal('tasks', decided);
    return decided;
  }

  private async reopenTaskOnline(input: { taskId: string; membershipId: string | null; actorUserId: string; reason: string | null }): Promise<Row<'tasks'>> {
    const requestId = this.commandContext().requestId;
    const data = input.membershipId === null
      ? await this.rpc('platform_support_reopen_task', { p_task_id: input.taskId, p_reason: input.reason, p_request_id: requestId })
      : await this.rpc('reopen_task', { p_task_id: input.taskId, p_membership_id: input.membershipId, p_reason: input.reason, p_request_id: requestId });
    const reopened = mapResult<Row<'tasks'>>(data);
    void mirrorRowToLocal('tasks', reopened);
    return reopened;
  }

  private async cancelTaskOnline(input: { taskId: string; membershipId: string | null; actorUserId: string; reason: string | null }): Promise<Row<'tasks'>> {
    const requestId = this.commandContext().requestId;
    const data = input.membershipId === null
      ? await this.rpc('platform_support_cancel_task', { p_task_id: input.taskId, p_reason: input.reason, p_request_id: requestId })
      : await this.rpc('cancel_task', { p_task_id: input.taskId, p_membership_id: input.membershipId, p_reason: input.reason, p_request_id: requestId });
    const cancelled = mapResult<Row<'tasks'>>(data);
    void mirrorRowToLocal('tasks', cancelled);
    return cancelled;
  }

  private async deleteTaskOnline(input: { taskId: string; actorUserId: string }): Promise<void> {
    const result = mapResult<{ storagePaths?: string[] }>(
      await this.rpc('delete_task', {
        p_task_id: input.taskId,
        p_request_id: this.commandContext().requestId,
      }),
    );
    const paths = (result?.storagePaths ?? []).filter((p): p is string => typeof p === 'string' && p.length > 0);
    if (paths.length > 0) {
      await this.client.storage.from(BUCKET).remove(paths).catch(() => null);
    }
    void removeRowFromLocal('tasks', input.taskId);
  }

  private async uploadAttachmentRecordOnline(attachment: Attachment, bytes: ArrayBuffer): Promise<Attachment> {
    const { error: uploadError } = await this.client.storage
      .from(BUCKET)
      .upload(attachment.storagePath, bytes, { contentType: attachment.mimeType, upsert: true });
    if (uploadError) {
      throw new AppError('storage_error', {
        userMessage: 'تعذّر رفع الملف. تحقق من الاتصال وحاول مجددًا.',
        cause: uploadError,
      });
    }
    const saved = await (this.table('attachments') as SupabaseRepository<'attachments'>).insertOnline(attachment);
    return saved;
  }

  private async deleteAttachmentOnline(input: { attachmentId: string; actorUserId: string; mode: 'manual' }): Promise<void> {
    const begin = mapResult<{ deletionId: string; storagePath: string }>(
      await this.rpc('begin_attachment_deletion', {
        p_attachment_id: input.attachmentId,
        p_request_id: this.commandContext().requestId,
      }),
    );
    const { error } = await this.client.storage.from(BUCKET).remove([begin.storagePath]);
    await this.rpc('finalize_attachment_deletion', {
      p_attachment_id: input.attachmentId,
      p_deletion_id: begin.deletionId,
      p_success: !error,
    });
    if (error) {
      throw new AppError('storage_error', {
        userMessage: 'تعذّر حذف الملف من التخزين. أُعيدت المحاولة لاحقًا.',
        cause: error,
      });
    }
    void removeLocalBlob(begin.storagePath);
    void removeRowFromLocal('attachments', input.attachmentId);
  }

  private createCommands(): GatewayCommands {
    return {
      createTask: async (input) => {
        if (!isBrowserOnline()) {
          const companyId = await this.resolveOfflineCompanyId({
            categoryId: input.task.categoryId,
            actorMembershipId: input.actorMembershipId,
          });
          return offlineCreateTask(input, companyId);
        }
        try {
          return await this.createTaskOnline(input);
        } catch (error) {
          if (isNetworkError(error)) {
            const companyId = await this.resolveOfflineCompanyId({
              categoryId: input.task.categoryId,
              actorMembershipId: input.actorMembershipId,
            });
            return offlineCreateTask(input, companyId);
          }
          throw error;
        }
      },

      updateTask: async (input) => {
        if (!isBrowserOnline()) return offlineUpdateTask(input);
        try {
          return await this.updateTaskOnline(input);
        } catch (error) {
          if (isNetworkError(error)) return offlineUpdateTask(input);
          throw error;
        }
      },

      addMembership: async (input) => {
        // أي حساب جديد يُنشأ خادميًا. تتحقق الوظيفة من صلاحية مدير المنشأة
        // أو من جلسة دعم المالك، ولا تُستخدم service role داخل المتصفح.
        if (input.password) {
          const { data, error } = await this.client.functions.invoke<{
            ok?: boolean;
            membership?: DbRow;
            error?: string;
          }>('create-company-member', {
            body: {
              companyId: input.companyId,
              email: input.email,
              fullName: input.fullName,
              jobTitle: input.jobTitle,
              primaryBranchId: input.primaryBranchId,
              roleIds: input.roleIds,
              password: input.password,
            },
          });
          if (error) {
            let message = 'تعذّر إنشاء الحساب. تحقق من نشر وظيفة إنشاء أعضاء المنشآت.';
            const context = (error as { context?: unknown }).context;
            if (context instanceof Response) {
              const payload = await context.clone().json().catch(() => null) as { error?: unknown } | null;
              if (typeof payload?.error === 'string') message = payload.error;
            }
            throw new AppError('unknown', { userMessage: message, cause: error });
          }
          if (!data) throw new AppError('unknown', { userMessage: 'لم يُرجع خادم إنشاء الحساب استجابة صالحة.' });
          if (data.error) throw new AppError('unknown', { userMessage: data.error });
          if (!data.membership) throw new AppError('unknown', { userMessage: 'لم يُرجع الخادم العضوية بعد إنشاء الحساب.' });
          return mapResult<Row<'memberships'>>(data.membership);
        }

        const data = await this.rpc('add_membership_by_email', {
          p_company_id: input.companyId,
          p_email: input.email,
          p_full_name: input.fullName,
          p_job_title: input.jobTitle,
          p_primary_branch_id: input.primaryBranchId,
          p_role_ids: input.roleIds,
          p_actor_membership_id: input.actorMembershipId,
        });
        return mapResult<Row<'memberships'>>(data);
      },

      completeTask: async (input) => {
        if (!isBrowserOnline()) return offlineCompleteTask(input);
        try {
          return await this.completeTaskOnline(input);
        } catch (error) {
          if (isNetworkError(error)) return offlineCompleteTask(input);
          throw error;
        }
      },

      decideApproval: async (input) => {
        if (!isBrowserOnline()) return offlineDecideApproval(input);
        try {
          return await this.decideApprovalOnline(input);
        } catch (error) {
          if (isNetworkError(error)) return offlineDecideApproval(input);
          throw error;
        }
      },

      reopenTask: async (input) => {
        if (!isBrowserOnline()) return offlineReopenTask(input);
        try {
          return await this.reopenTaskOnline(input);
        } catch (error) {
          if (isNetworkError(error)) return offlineReopenTask(input);
          throw error;
        }
      },

      cancelTask: async (input) => {
        if (!isBrowserOnline()) return offlineCancelTask(input);
        try {
          return await this.cancelTaskOnline(input);
        } catch (error) {
          if (isNetworkError(error)) return offlineCancelTask(input);
          throw error;
        }
      },

      deleteTask: async (input) => {
        if (!isBrowserOnline()) return offlineDeleteTask(input);
        try {
          await this.deleteTaskOnline(input);
        } catch (error) {
          if (isNetworkError(error)) return offlineDeleteTask(input);
          throw error;
        }
      },

      purgeCompletedTasks: async (input) => {
        const result = mapResult<{
          tasksCount: number;
          filesCount: number;
          bytesFreed: number;
          storagePaths?: string[];
        }>(
          await this.rpc('purge_completed_tasks', {
            p_company_id: input.companyId,
            p_older_than_days: input.olderThanDays,
            p_dry_run: input.dryRun ?? false,
            p_request_id: this.commandContext().requestId,
          }),
        );
        const paths = (result?.storagePaths ?? []).filter((p): p is string => typeof p === 'string' && p.length > 0);
        if (!input.dryRun && paths.length > 0) {
          await this.client.storage.from(BUCKET).remove(paths).catch(() => null);
        }
        return {
          tasksCount: Number(result?.tasksCount ?? 0),
          filesCount: Number(result?.filesCount ?? 0),
          bytesFreed: Number(result?.bytesFreed ?? 0),
        };
      },

      assignTask: async (input) => {
        const requestId = this.commandContext().requestId;
        const data = input.actorMembershipId === null
          ? await this.rpc('platform_support_assign_task', { p_task_id: input.taskId, p_membership_ids: input.membershipIds, p_request_id: requestId })
          : await this.rpc('assign_task', { p_task_id: input.taskId, p_membership_ids: input.membershipIds, p_actor_membership_id: input.actorMembershipId, p_request_id: requestId });
        return mapResult<Row<'tasks'>>(data);
      },

      generateOccurrences: async (input) => {
        const data = mapResult<{ created: number; skipped: number; lastDate: string }>(
          await this.rpc('generate_occurrences', {
            p_template_id: input.templateId,
            p_through_date: input.throughDate,
            p_request_id: this.commandContext().requestId,
          }),
        );
        return { created: data.created, skipped: data.skipped, lastDate: data.lastDate };
      },

      createCompany: async (input) => {
        const data = await this.rpc('create_company', {
          p_name: input.company.name,
          p_slug: input.company.slug,
          p_time_zone: input.company.timeZone,
          p_locale: input.company.locale,
          p_currency: input.company.currency,
          p_settings: input.company.settings ?? {},
          p_limits: input.company.limits,
          p_seed_defaults: input.seedDefaults,
          p_request_id: this.commandContext().requestId,
          p_create_owner_membership: input.createOwnerMembership ?? true,
        });
        return mapResult<Row<'companies'>>(data);
      },

      manageOrgEntity: async (input) => {
        if (input.entityType === 'membership' && input.action === 'restore') {
          await this.rpc('restore_org_membership', {
            p_membership_id: input.entityId,
            p_reason: input.reason ?? null,
          });
        } else {
          await this.rpc('manage_org_entity', {
            p_entity_type: input.entityType,
            p_entity_id: input.entityId,
            p_action: input.action,
            p_scope_type: input.scopeType ?? 'company',
            p_scope_id: input.scopeId ?? null,
            p_reason: input.reason ?? null,
            p_confirmation: input.confirmation ?? null,
          });
        }
      },

      listOrgArchive: async (input) => {
        const data = await this.rpc<import('../types').ArchivedOrgRecord[] | null>('list_org_archive', {
          p_entity_type: input.entityType,
          p_scope_type: input.scopeType,
          p_scope_id: input.scopeId ?? null,
        });
        return data ?? [];
      },

      updateMembershipProfile: async (input) => {
        await this.rpc('update_company_member_profile', {
          p_company_id: input.companyId,
          p_membership_id: input.membershipId,
          p_full_name: input.fullName,
          p_job_title: input.jobTitle,
        });
      },

      updateMemberAccountAuth: async (input) => {
        const { data, error } = await this.client.functions.invoke<{
          ok?: boolean;
          email?: string;
          emailChanged?: boolean;
          passwordChanged?: boolean;
          auditWarning?: string;
          error?: string;
        }>('update-company-member-account', { body: input });
        if (error) {
          let message = 'تعذّر تحديث بيانات دخول العضو.';
          const context = (error as { context?: unknown }).context;
          if (context instanceof Response) {
            const payload = await context.clone().json().catch(() => null) as { error?: unknown } | null;
            if (typeof payload?.error === 'string') message = payload.error;
          }
          throw new AppError('unknown', { userMessage: message, cause: error });
        }
        if (!data?.ok) {
          throw new AppError('unknown', { userMessage: data?.error ?? 'لم يُرجع الخادم نتيجة صالحة لتحديث الحساب.' });
        }
        return {
          email: data.email ?? '',
          emailChanged: data.emailChanged === true,
          passwordChanged: data.passwordChanged === true,
          ...(data.auditWarning ? { auditWarning: data.auditWarning } : {}),
        };
      },

      purgeAuditLogs: async (input) => this.rpc(
        'purge_platform_audit_logs',
        { p_confirmation: input.confirmation },
        'تعذّر تأكيد نتيجة حذف سجل نشاط المنصة. تحقق من حالة السجل قبل إعادة المحاولة.',
      ),

      purgeCompanyActivityLogs: async (input) => mapResult(
        await this.rpc('purge_company_activity_logs', {
          p_company_id: input.companyId,
          p_confirmation: input.confirmation,
        }, 'تعذّر تأكيد نتيجة حذف سجل نشاط المنشأة. تحقق من حالة السجل قبل إعادة المحاولة.'),
      ),

      listPlatformActivity: async (input = {}) => rowsFromDb<PlatformActivityEvent>(
        mapRowsResult<DbRow>(await this.rpc('list_platform_activity', {
          p_limit: input.limit ?? 300,
        })),
      ),

      getPlatformActivityRetention: async () => mapResult<PlatformActivityRetentionSettings>(
        await this.rpc('get_platform_activity_retention'),
      ),

      setPlatformActivityRetention: async (input) => mapResult<PlatformActivityRetentionSettings>(
        await this.rpc('set_platform_activity_retention', {
          p_enabled: input.enabled,
          p_retention_days: input.retentionDays,
        }),
      ),

      bootstrapStatus: async () => {
        const data = mapResult<{
          initialized: boolean;
          canBootstrap: boolean;
          showBootstrapLink: boolean;
          reason: string;
          branding?: Partial<PlatformBranding> | null;
          theme?: ThemeTokens | null;
          selfRegistrationEnabled?: boolean;
          subscriptionsEnabled?: boolean;
        }>(await this.rpc('bootstrap_status'));
        const branding: PlatformBranding = { ...DEFAULT_PLATFORM_BRANDING };
        for (const key of Object.keys(DEFAULT_PLATFORM_BRANDING) as Array<keyof PlatformBranding>) {
          const value = data.branding?.[key];
          if (typeof value === 'string' && value.trim().length > 0) branding[key] = value;
        }
        return {
          initialized: data.initialized,
          canBootstrap: data.canBootstrap,
          showBootstrapLink: data.showBootstrapLink !== false,
          method: 'server_function',
          reason: data.reason,
          branding,
          theme: data.theme ?? null,
          selfRegistrationEnabled: data.selfRegistrationEnabled === true,
          subscriptionsEnabled: data.subscriptionsEnabled === true,
        };
      },

      setBootstrapLinkVisibility: async (input) => {
        await this.rpc('set_bootstrap_link_visibility', { p_visible: input.visible });
      },

      setPlatformBranding: async (input) => {
        await this.rpc('set_platform_branding', {
          p_branding: input.branding,
          p_theme: input.theme,
        });
      },

      getPlatformOperationsSettings: async () => {
        return mapResult<PlatformOperationsSettings>(
          await this.rpc('get_platform_operations_settings'),
        );
      },

      setPlatformOperationsSettings: async (input) => {
        return mapResult<PlatformOperationsSettings>(
          await this.rpc('set_platform_operations_settings', {
            p_self_registration_enabled: input.selfRegistrationEnabled,
            p_subscriptions_enabled: input.subscriptionsEnabled,
            p_payments_enabled: input.paymentsEnabled,
            p_default_trial_days: input.defaultTrialDays,
            p_default_plan_id: input.defaultPlanId,
          }),
        );
      },

      selfRegisterCompany: async (input) => {
        const data = await this.rpc('create_company_self_service', {
          p_name: input.name,
          p_slug: input.slug,
          p_time_zone: input.timeZone,
          p_locale: input.locale,
          p_currency: input.currency,
          p_request_id: input.requestId,
        });
        return mapResult<Row<'companies'>>(data);
      },

      createCompanyInvite: async (input) => {
        return mapResult<import('../types').CreateCompanyInviteResult>(
          await this.rpc('create_company_invite', {
            p_company_id: input.companyId,
            p_email: input.email,
            p_role_id: input.roleId ?? null,
            p_expires_hours: input.expiresHours ?? 168,
          }),
        );
      },

      acceptCompanyInvite: async (input) => {
        return mapResult<import('../types').AcceptCompanyInviteResult>(
          await this.rpc('accept_company_invite', { p_token: input.token }),
        );
      },

      saveSubscriptionPlan: async (input) => {
        return mapResult<Row<'subscription_plans'>>(
          await this.rpc('upsert_subscription_plan', {
            p_plan_id: input.planId,
            p_code: input.code,
            p_name: input.name,
            p_description: input.description,
            p_price_cents: input.priceCents,
            p_currency: input.currency,
            p_billing_interval: input.billingInterval,
            p_trial_days: input.trialDays,
            p_features: input.features,
            p_is_active: input.isActive,
            p_sort_order: input.sortOrder,
          }),
        );
      },

      saveCompanySubscription: async (input) => {
        return mapResult<Row<'company_subscriptions'>>(
          await this.rpc('upsert_company_subscription', {
            p_company_id: input.companyId,
            p_plan_id: input.planId,
            p_status: input.status,
            p_starts_at: input.startsAt,
            p_ends_at: input.endsAt,
            p_trial_ends_at: input.trialEndsAt,
            p_cancel_at_period_end: input.cancelAtPeriodEnd,
            p_source: input.source,
            p_notes: input.notes,
          }),
        );
      },

      recordSubscriptionPayment: async (input) => {
        return mapResult<Row<'subscription_payments'>>(
          await this.rpc('record_subscription_payment', {
            p_company_id: input.companyId,
            p_subscription_id: input.subscriptionId,
            p_status: input.status,
            p_amount_cents: input.amountCents,
            p_currency: input.currency,
            p_due_at: input.dueAt,
            p_paid_at: input.paidAt,
            p_provider: input.provider,
            p_provider_ref: input.providerRef,
            p_client_ref: input.clientRef,
            p_notes: input.notes,
          }),
        );
      },

      updateSubscriptionPayment: async (input) => {
        return mapResult<Row<'subscription_payments'>>(
          await this.rpc('update_subscription_payment', {
            p_payment_id: input.paymentId,
            p_status: input.status,
            p_paid_at: input.paidAt,
            p_notes: input.notes,
          }),
        );
      },

      getRoleDeletionImpact: async (input) => mapResult<RoleDeletionImpact>(
        await this.rpc('company_role_deletion_impact', {
          p_company_id: input.companyId,
          p_role_id: input.roleId,
        }),
      ),

      deleteCompanyRole: async (input) => {
        await this.rpc('delete_company_role', {
          p_company_id: input.companyId,
          p_role_id: input.roleId,
          p_expected_member_ids: input.expectedMemberIds,
          p_expected_dependent_role_ids: input.expectedDependentRoleIds,
        });
      },

      listDeletedCompanyRoles: async (input) => rowsFromDb<Role>(
        mapRowsResult<DbRow>(await this.rpc('list_deleted_company_roles', {
          p_company_id: input.companyId,
        })),
      ),

      restoreCompanyRole: async (input) => mapResult<Role>(
        await this.rpc('restore_company_role', {
          p_company_id: input.companyId,
          p_role_id: input.roleId,
        }),
      ),

      listCompanyProfiles: async (input) => {
        const cacheKey = `taskflow.company_profiles:${input.companyId}`;
        const readCached = () => {
          try {
            const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(cacheKey) : null;
            return raw ? JSON.parse(raw) : [];
          } catch {
            return [];
          }
        };
        if (!isBrowserOnline()) return readCached();
        try {
          const data = mapRowsResult<
            { membership_id: string; user_id: string; full_name: string; phone: string | null; avatar_data_url: string | null }
          >(await this.rpc('company_member_profiles', { p_company_id: input.companyId }));
          const list = data.map((row) => ({
            membershipId: row.membership_id,
            userId: row.user_id,
            fullName: row.full_name,
            phone: row.phone,
            avatarDataUrl: row.avatar_data_url,
          }));
          try {
            if (typeof localStorage !== 'undefined') localStorage.setItem(cacheKey, JSON.stringify(list));
          } catch {}
          return list;
        } catch (error) {
          if (isNetworkError(error)) return readCached();
          throw error;
        }
      },

      listCompanyMemberEmails: async (input) => mapRowsResult<{ membership_id: string; email: string }>(
        await this.rpc('company_member_emails', { p_company_id: input.companyId }),
      ).map((row) => ({ membershipId: row.membership_id, email: row.email })),
      listPlatformMembers: async () => mapRowsResult<{ user_id: string; email: string; full_name: string; role_name: string; status: 'active' | 'suspended'; permissions: PermissionKey[] }>(
        await this.rpc('list_platform_members'),
      ).map((row) => ({ userId: row.user_id, email: row.email, fullName: row.full_name, roleName: row.role_name ?? 'مخصص', status: row.status, permissions: Array.isArray(row.permissions) ? row.permissions : [] })),
      savePlatformMember: async (input) => { await this.rpc('save_platform_member_with_role', { p_email: input.email, p_permissions: input.permissions, p_role_name: input.roleName ?? 'مخصص' }); },
      createPlatformMember: async (input) => {
        const { error } = await this.client.functions.invoke('create-platform-member', { body: input });
        if (error) throw new AppError('unknown', { userMessage: 'تعذّر إنشاء الحساب. تحقق من نشر وظيفة إنشاء أعضاء فريق المنصة.', cause: error });
      },
      removePlatformMember: async (input) => { await this.rpc('remove_platform_member', { p_user_id: input.userId }); },
      bootstrapPlatformOwner: async (input) => {
        // 1) نحاول الدخول أولًا: إن كان الحساب موجودًا ومؤكدًا فهذا هو المسار الصحيح.
        //    (Supabase يُعيد استجابة مبهمة لـ signUp على بريد موجود عند تفعيل تأكيد
        //     البريد، فلا يصلح الاعتماد على signUp أولًا لكشف وجود الحساب.)
        let userId = '';
        let signInError: unknown = null;
        try {
          const signIn = await this.auth.signIn(input.email, input.password);
          userId = signIn.userId;
        } catch (error) {
          signInError = error;
        }

        if (!userId) {
          // 2) لا دخول: إما حساب جديد أو كلمة مرور خاطئة/بريد غير مؤكد.
          try {
            const signUp = await this.auth.signUp({
              email: input.email,
              password: input.password,
              fullName: input.fullName,
            });
            userId = signUp.userId;
            if (signUp.requiresEmailConfirmation) {
              throw AppError.unsupported(
                'تم إنشاء الحساب أو كان موجودًا. أكمل تأكيد البريد الإلكتروني ثم أعد المحاولة. إن كان الحساب مؤكدًا فتحقق من كلمة المرور.',
              );
            }
          } catch (error) {
            if (error instanceof AppError && error.code === 'unsupported') throw error;
            if (signInError instanceof Error) throw signInError;
            throw AppError.unauthenticated('تعذّر الدخول أو إنشاء حساب المالك.');
          }
        }

        const session = await this.auth.getSession();
        if (!session) {
          await this.auth.signIn(input.email, input.password);
        }
        // 3) المطالبة بالملكية عبر رمز يُتحقق منه على الخادم فقط.
        await this.rpc('bootstrap_platform_owner', {
          p_bootstrap_token: input.bootstrapToken,
          p_full_name: input.fullName,
        });
        const ownerSession = await this.auth.getSession();
        return {
          userId: ownerSession?.userId ?? userId,
          email: input.email,
          requiresEmailConfirmation: false,
        };
      },

      cancelTemplateOccurrences: async (input) => {
        const requestId = this.commandContext().requestId;
        const count = input.actorMembershipId === null
          ? await this.rpc<number>('platform_support_cancel_template_occurrences', {
              p_template_id: input.templateId, p_from_date: input.fromDate, p_reason: input.reason, p_request_id: requestId,
            })
          : await this.rpc<number>('cancel_template_occurrences', {
              p_template_id: input.templateId, p_from_date: input.fromDate, p_actor_membership_id: input.actorMembershipId,
              p_reason: input.reason, p_request_id: requestId,
            });
        return Number(count ?? 0);
      },

      applyRetention: async (input) => {
        const preview = mapResult<{
          filesCount: number;
          bytesFreed: number;
          attachmentIds: string[];
        }>(
          await this.rpc('retention_preview', {
            p_company_id: input.companyId,
            p_scope: input.scope ?? {},
          }),
        );

        if (input.dryRun) {
          return {
            filesCount: preview.filesCount,
            bytesFreed: preview.bytesFreed,
            attachmentIds: preview.attachmentIds,
            deletionId: null,
          };
        }

        const begin = mapResult<{
          deletionId: string;
          files: Array<{ attachmentId: string; storagePath: string; sizeBytes: number }>;
        }>(
          await this.rpc('retention_begin', {
            p_company_id: input.companyId,
            p_scope: input.scope ?? {},
            p_request_id: this.commandContext().requestId,
          }),
        );

        const paths = begin.files.map((file) => file.storagePath);
        let failedIds: string[] = [];
        if (paths.length > 0) {
          const { data: removed, error } = await this.client.storage.from(BUCKET).remove(paths);
          const removedPaths = new Set((removed ?? []).map((item) => item.name));
          failedIds = error
            ? begin.files.map((file) => file.attachmentId)
            : begin.files
                .filter((file) => !removedPaths.has(file.storagePath))
                .map((file) => file.attachmentId);
        }

        await this.rpc('retention_finalize', {
          p_deletion_id: begin.deletionId,
          p_failed_attachment_ids: failedIds,
        });

        const successful = begin.files.filter((file) => !failedIds.includes(file.attachmentId));
        return {
          filesCount: successful.length,
          bytesFreed: successful.reduce((sum, file) => sum + file.sizeBytes, 0),
          attachmentIds: begin.files.map((file) => file.attachmentId),
          deletionId: begin.deletionId,
        };
      },

      uploadAttachment: async (input) => {
        const session = await this.auth.getSession();
        const userId = session?.userId ?? input.uploadedByUserId;
        if (!userId) throw AppError.unauthenticated();
        if (!DEFAULT_UPLOAD_POLICY.allowedMimeTypes.includes(input.mimeType)) {
          throw AppError.validation('نوع الملف غير مسموح');
        }
        if (input.bytes.byteLength > DEFAULT_UPLOAD_POLICY.maxSizeMb * 1024 * 1024) {
          throw AppError.validation(`حجم الملف يتجاوز الحد (${DEFAULT_UPLOAD_POLICY.maxSizeMb} ميجابايت)`);
        }

        if (!isBrowserOnline()) {
          return offlineUploadAttachment(input, userId);
        }

        try {
          const existingResult = await run(
            this.client
              .from('attachments')
              .select('*')
              .eq('client_ref', input.clientRef)
              .eq('uploaded_by_user_id', userId)
              .is('deleted_at', null)
              .maybeSingle() as unknown as PromiseLike<DbResponse<DbRow | null>>,
            'تعذّر التحقق من المرفق.',
          );
          if (existingResult.data) return rowFromDb<Attachment>(existingResult.data);

          const attachmentId = newId();
          const storagePath = buildStoragePath({
            companyId: input.companyId,
            taskId: input.taskId,
            attachmentId,
            fileName: input.fileName,
          });

          // حفظ نسخة في الجهاز لعرض الملف فوراً بـ 0ms حتى لو انقطع الإنترنت لاحقاً
          void saveLocalBlob(storagePath, input.bytes, input.mimeType);

          const { error: uploadError } = await this.client.storage
            .from(BUCKET)
            .upload(storagePath, input.bytes, { contentType: input.mimeType, upsert: false });
          if (uploadError) {
            if (isNetworkError(uploadError)) {
              return offlineUploadAttachment(input, userId);
            }
            throw new AppError('storage_error', {
              userMessage: 'تعذّر رفع الملف. تحقق من الاتصال وحاول مجددًا.',
              cause: uploadError,
            });
          }

          const checksum = await sha256Hex(input.bytes);
          const now = new Date().toISOString();
          try {
            return await (this.table('attachments') as SupabaseRepository<'attachments'>).insertOnline({
              id: attachmentId,
              companyId: input.companyId,
              taskId: input.taskId,
              taskAssigneeId: input.taskAssigneeId,
              storagePath,
              fileName: input.fileName,
              mimeType: input.mimeType,
              sizeBytes: input.bytes.byteLength,
              kind: input.kind,
              uploadState: 'uploaded',
              uploadedByUserId: userId,
              checksum,
              clientRef: input.clientRef,
              retentionUntil: null,
              deletedAt: null,
              deletionId: null,
              createdAt: now,
              updatedAt: now,
            });
          } catch (error) {
            await this.client.storage.from(BUCKET).remove([storagePath]);
            throw error;
          }
        } catch (error) {
          if (isNetworkError(error)) {
            return offlineUploadAttachment(input, userId);
          }
          throw error;
        }
      },

      deleteAttachment: async (input) => {
        if (!isBrowserOnline()) return offlineDeleteAttachment(input);
        try {
          await this.deleteAttachmentOnline(input);
        } catch (error) {
          if (isNetworkError(error)) return offlineDeleteAttachment(input);
          throw error;
        }
      },

      createAttachmentUrl: async (input) => {
        const attachment = await this.table('attachments').get(input.attachmentId);
        if (!attachment || attachment.deletedAt || attachment.uploadState !== 'uploaded') {
          throw AppError.notFound('الملف غير متاح');
        }
        // أولوية للنسخة المخزنة محلياً لسرعة فتح فورية والعمل بدون إنترنت
        const localUrl = await getLocalBlobUrl(attachment.storagePath);
        if (localUrl) return localUrl;

        const { data, error } = await this.client.storage
          .from(BUCKET)
          .createSignedUrl(attachment.storagePath, Math.min(Math.max(input.expiresInSeconds, 30), 3600));
        if (error || !data?.signedUrl) {
          throw new AppError('storage_error', {
            userMessage: 'تعذّر إنشاء رابط آمن للملف.',
            cause: error,
          });
        }
        return data.signedUrl;
      },

      markNotificationRead: async (input) => {
        await this.rpc('mark_notification_read', {
          p_notification_id: input.notificationId,
        });
      },

      markAllNotificationsRead: async (input) => {
        const count = await this.rpc<number>('mark_all_notifications_read', {
          p_company_id: input.companyId,
        });
        return Number(count ?? 0);
      },

      sweepMyNotifications: async (input) => {
        if (!isBrowserOnline()) return 0;
        try {
          const count = await this.rpc<number>('sweep_my_notifications', {
            p_company_id: input.companyId,
            p_within_hours: input.withinHours,
          });
          return Number(count ?? 0);
        } catch {
          return 0;
        }
      },
    };
  }
}
